// src/components/landing/LandingNavbar.jsx
import React, { useContext, useState, useEffect } from 'react';
import {
  Box, Button, IconButton, Drawer,
  Stack, Divider, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, LightMode, DarkMode } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import bssLogo from '../../assets/images/bsslogo.jpeg';

const NAV_LINKS = [
  { label: 'About',        href: '#about' },
  { label: 'Courses',      href: '#courses' },
  { label: 'Affiliations', href: '#affiliations' },
  { label: 'Gallery',      href: '#gallery' },
  { label: 'Contact',      href: '#contact' },
];

const PHONE = '0471-2335853 / 9188225979';

export default function LandingNavbar({ onEnroll, onFranchise }) {
  const { mode, toggleTheme } = useContext(ThemeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* ── Top announcement strip ── */}
      <Box sx={{
        background: 'linear-gradient(90deg,#1d4ed8,#1d6cf0)',
        py: 0.7, px: { xs: 2, md: 6 },
        display: 'flex', alignItems: 'center',
        justifyContent: { xs: 'center', md: 'space-between' },
        gap: 2, zIndex: 1201, position: 'relative',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* pulsing dot */}
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%', bgcolor: '#4ade80',
            boxShadow: '0 0 0 2px rgba(74,222,128,0.25)',
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': { '0%,100%': { boxShadow: '0 0 0 2px rgba(74,222,128,0.25)' }, '50%': { boxShadow: '0 0 0 6px rgba(74,222,128,0)' } },
          }} />
          <Typography
            component="a" href={`tel:${PHONE.replace(/\s/g, '')}`}
            sx={{ fontSize: '0.73rem', color: '#fff', fontWeight: 700, letterSpacing: 0.4, textDecoration: 'none', '&:hover': { color: '#bae6fd' } }}
          >
            {PHONE}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', display: { xs: 'none', md: 'block' } }}>
          Govt.-Approved IT Education &nbsp;·&nbsp; 150+ Centers Across Kerala
        </Typography>
      </Box>

      {/* ── Main sticky navbar ── */}
      <Box
        component="nav"
        sx={{
          position: 'sticky', top: 0, zIndex: 1200,
          backgroundColor: scrolled ? 'rgba(8,12,26,0.98)' : 'rgba(10,15,30,0.93)',
          backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
          borderBottom: scrolled ? '1px solid rgba(56,189,248,0.18)' : '1px solid rgba(255,255,255,0.07)',
          boxShadow: scrolled ? '0 4px 32px rgba(0,0,0,0.4)' : 'none',
          transition: 'background-color 0.3s, box-shadow 0.3s, border-color 0.3s',
        }}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center',
          px: { xs: 2, md: 6 }, height: 70,
        }}>

          {/* ── Logo ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, textDecoration: 'none' }}
            component="a" href="/">
            <Box sx={{
              position: 'relative',
              '&::after': {
                content: '""', position: 'absolute', inset: -2, borderRadius: '50%',
                background: 'conic-gradient(#38bdf8,#1d6cf0,#38bdf8)',
                zIndex: -1, opacity: 0.6, filter: 'blur(4px)',
              },
            }}>
              <Box
                component="img"
                src={bssLogo}
                alt="BSS IT Mission"
                sx={{ height: 44, width: 44, objectFit: 'cover', borderRadius: '50%', display: 'block', border: '2px solid rgba(56,189,248,0.35)' }}
              />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, fontSize: { sm: '0.9rem', lg: '1.05rem' }, lineHeight: 1.2, color: '#fff', letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
                BSS Information Technology <Box component="span" sx={{ color: '#38bdf8' }}>MISSION</Box>
              </Typography>
              <Typography sx={{ fontSize: '0.57rem', color: 'rgba(255,255,255,0.35)', letterSpacing: 2.5, textTransform: 'uppercase', mt: 0.15, whiteSpace: 'nowrap' }}>
                Bharat Sevak Samaj · Kerala
              </Typography>
            </Box>
          </Box>

          {/* ── Desktop nav links ── */}
          {!isMobile && (
            <Stack direction="row" spacing={0} alignItems="center" sx={{ mr: { md: 1, lg: 4 } }}>
              {NAV_LINKS.map((l) => (
                <Box
                  key={l.href}
                  component="a"
                  href={l.href}
                  sx={{
                    px: { md: 1.25, lg: 2 }, py: 0.75, fontSize: { md: '0.78rem', lg: '0.84rem' }, color: 'rgba(255,255,255,0.55)',
                    textDecoration: 'none', fontWeight: 500, letterSpacing: 0.3,
                    position: 'relative',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: '#fff',
                      '&::after': { width: '100%', opacity: 1 },
                    },
                    '&::after': {
                      content: '""', position: 'absolute', bottom: 0, left: 0,
                      width: 0, height: '2px',
                      background: '#38bdf8',
                      opacity: 0, transition: 'width 0.22s ease, opacity 0.22s ease',
                    },
                  }}
                >
                  {l.label}
                </Box>
              ))}
            </Stack>
          )}

          {/* ── Desktop actions ── */}
          {!isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Phone */}
              <Box
                component="a" href={`tel:${PHONE.replace(/\s/g, '')}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 0.6, textDecoration: 'none',
                  borderLeft: '2px solid #38bdf8',
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2.5">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.58 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.27a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/>
                </svg>
                <Typography sx={{ fontSize: { md: '0.68rem', lg: '0.75rem' }, color: '#7dd3fc', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {PHONE}
                </Typography>
              </Box>

              <Box sx={{ width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

              <IconButton onClick={toggleTheme} size="small" sx={{
                color: 'rgba(255,255,255,0.4)', borderRadius: 0,
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
              }}>
                {mode === 'light' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
              </IconButton>

              <Button onClick={() => navigate('/login')} sx={{
                color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 0,
                px: 2, py: 0.75, minWidth: 90, whiteSpace: 'nowrap', lineHeight: 1.5,
                '&:hover': { color: '#fff', border: '1px solid rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                transition: 'all 0.2s',
              }}>
                Login
              </Button>

              <Button onClick={onFranchise} sx={{
                color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 0,
                px: 2, py: 0.75, minWidth: 90, whiteSpace: 'nowrap', lineHeight: 1.5,
                '&:hover': { color: '#fff', border: '1px solid rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                transition: 'all 0.2s',
              }}>
                Franchise
              </Button>

              <Button onClick={onEnroll} variant="contained" sx={{
                background: 'linear-gradient(135deg,#1d6cf0 0%,#0ea5e9 100%)',
                borderRadius: 0, px: 2, py: 0.75, textTransform: 'none',
                fontSize: '0.82rem', fontWeight: 700, letterSpacing: 0.3,
                minWidth: 110, whiteSpace: 'nowrap', lineHeight: 1.5,
                boxShadow: '0 0 0 1px rgba(29,108,240,0.6)',
                '&:hover': { boxShadow: '0 4px 20px rgba(29,108,240,0.6)', transform: 'translateY(-1px)' },
                transition: 'all 0.2s',
              }}>
                Enroll Now
              </Button>
            </Stack>
          )}

          {/* Mobile icons */}
          {isMobile && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="a" href={`tel:${PHONE.replace(/\s/g, '')}`}
                sx={{ display: 'flex', p: 1, color: '#38bdf8' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.58 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.27a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/>
                </svg>
              </Box>
              <IconButton onClick={toggleTheme} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {mode === 'light' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
              </IconButton>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#fff' }}>
                <MenuIcon />
              </IconButton>
            </Stack>
          )}
        </Box>
      </Box>

      {/* ── Mobile Drawer ── */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 290, bgcolor: '#0b1120', color: '#fff' } }}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box component="img" src={bssLogo} alt="logo"
                sx={{ height: 34, width: 34, borderRadius: '50%', border: '1.5px solid rgba(56,189,248,0.4)', objectFit: 'cover' }} />
              <Typography sx={{ fontWeight: 900, fontSize: '0.9rem' }}>
                BSS Information Technology <Box component="span" sx={{ color: '#38bdf8' }}>MISSION</Box>
              </Typography>
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Phone */}
          <Box component="a" href={`tel:${PHONE.replace(/\s/g, '')}`} sx={{
            display: 'flex', alignItems: 'center', gap: 1.25,
            bgcolor: 'rgba(56,189,248,0.06)', borderLeft: '3px solid #38bdf8',
            px: 2, py: 1.25, mb: 3, textDecoration: 'none',
            '&:hover': { bgcolor: 'rgba(56,189,248,0.1)' },
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2.2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.58 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.27a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/>
            </svg>
            <Typography sx={{ fontSize: '0.85rem', color: '#7dd3fc', fontWeight: 700 }}>{PHONE}</Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2 }} />

          {/* Nav links */}
          <Stack spacing={0} sx={{ mb: 2 }}>
            {NAV_LINKS.map((l) => (
              <Button key={l.href} fullWidth href={l.href} onClick={() => setDrawerOpen(false)}
                sx={{
                  color: 'rgba(255,255,255,0.65)', justifyContent: 'flex-start',
                  textTransform: 'none', py: 1.2, px: 2, borderRadius: 0,
                  fontSize: '0.88rem', borderBottom: '1px solid rgba(255,255,255,0.04)',
                  '&:hover': { bgcolor: 'rgba(56,189,248,0.06)', color: '#fff', borderLeft: '2px solid #38bdf8', pl: 1.75 },
                  transition: 'all 0.15s',
                }}>
                {l.label}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2 }} />

          <Stack spacing={1}>
            <Button fullWidth onClick={() => { setDrawerOpen(false); navigate('/login'); }}
              sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 0, textTransform: 'none', py: 1.2, fontSize: '0.88rem' }}>
              Login
            </Button>
            <Button fullWidth onClick={() => { setDrawerOpen(false); onFranchise(); }}
              sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 0, textTransform: 'none', py: 1.2, fontSize: '0.88rem' }}>
              Franchise
            </Button>
            <Button fullWidth onClick={() => { setDrawerOpen(false); onEnroll(); }} variant="contained"
              sx={{ background: 'linear-gradient(135deg,#1d6cf0,#0ea5e9)', borderRadius: 0, textTransform: 'none', py: 1.25, fontWeight: 700, boxShadow: 'none' }}>
              Enroll Now
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
